import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Shield, ArrowLeft, Mail, Building, LogOut, MapPin, AlertTriangle } from 'lucide-react'
import PasskeySettings from '../components/PasskeySettings'
import { useProductTour } from '../hooks/useProductTour'

interface UserData {
  id: string
  email: string
  name: string
  company: string
  role: string
}

const UserProfilePage: React.FC = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState<UserData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [passkeyCount, setPasskeyCount] = useState(0)
  const { resetTour, tourStatus } = useProductTour('tenant', false)

  useEffect(() => {
    loadUserProfile()
    loadPasskeyCount()
  }, [])

  const loadUserProfile = async () => {
    try {
      const token = localStorage.getItem('auth-token')
      if (!token) {
        navigate('/login')
        return
      }

      const response = await fetch('/api/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUser(data.user)
        setError(null)
      } else if (response.status === 401 || response.status === 403) {
        // Only redirect on authentication/authorization errors
        navigate('/login')
      } else {
        // For other errors (network issues, server errors), show error UI
        setError(`Failed to load profile (${response.status}). Please try again.`)
      }
    } catch (error) {
      console.error('Failed to load profile:', error)
      // Network or other transient error - show retry option instead of redirecting
      setError('Network error loading profile. Please check your connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadPasskeyCount = async () => {
    try {
      const token = localStorage.getItem('auth-token')
      if (!token) return

      const response = await fetch('/api/auth/user/passkeys', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setPasskeyCount(data.passkeys?.length || 0)
      }
    } catch (error) {
      console.error('Failed to load passkey count:', error)
    }
  }

  const handleLogout = () => {
    // SECURITY: Clear ALL cached data including sensitive information
    localStorage.clear()
    sessionStorage.clear()

    // Clear all cookies
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim()
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    })

    navigate('/login')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    )
  }

  // Show error state with retry option instead of redirecting
  if (error && !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="mb-4">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Unable to Load Profile
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  setError(null)
                  setLoading(true)
                  loadUserProfile()
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Back to Trust Center
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Trust Center
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Profile</h1>
          <p className="text-gray-600">Manage your account settings and security preferences</p>
        </div>

        {/* Profile Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="h-5 w-5" />
              Account Information
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="h-4 w-4 inline mr-1" />
                Email
              </label>
              <div className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                {user.email}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="h-4 w-4 inline mr-1" />
                Name
              </label>
              <div className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                {user.name}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Building className="h-4 w-4 inline mr-1" />
                Company
              </label>
              <div className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                {user.company}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                To update your profile information, please contact support.
              </p>
            </div>
          </div>
        </div>

        {/* Passkey/Security Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Security & Authentication
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Manage your passkeys and authentication methods
                </p>
              </div>
              {passkeyCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  <Shield className="h-4 w-4" />
                  {passkeyCount} passkey{passkeyCount !== 1 ? 's' : ''} active
                </div>
              )}
            </div>
          </div>
          <div className="p-6">
            <PasskeySettings />
          </div>
        </div>

        {/* Product Tour */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Product Tour
            </h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 mb-4">
              Take a guided tour to learn about the features available in this trust center.
            </p>
            <div className="flex items-center justify-between">
              <div>
                {tourStatus?.hasCompletedTour && tourStatus.completedAt && (
                  <p className="text-sm text-gray-500">
                    Last completed: {new Date(tourStatus.completedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={resetTour}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <MapPin className="h-4 w-4" />
                {tourStatus?.hasCompletedTour ? 'Retake Tour' : 'Start Tour'}
              </button>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Need help?</h3>
          <p className="text-sm text-blue-700">
            If you have questions about your account or need to update your information,
            please contact the Trust Center administrator.
          </p>
        </div>
      </div>
    </div>
  )
}

export default UserProfilePage
